import { useEffect, useRef } from 'react';
import moment from "moment";
import { RappidNodeType, RappidText } from "helpers/Constants";
import { Enum } from "helpers";

export const calculateAddDelayDate = (weeks, days, hours, minutes, startDate = null) => {
    const date = startDate ?? moment();
    return moment(date)
        .add(days + (weeks * 7), 'days')
        .add(minutes + (hours * 60), 'minutes')
        .utc().format();
}

export const calculateSubstractDelayDate = (weeks, days, hours, minutes, startDate) => {
    return moment(startDate)
        .subtract(days + (weeks * 7), 'days')
        .subtract(minutes + (hours * 60), 'minutes')
        .utc().format();
}

export const convertDatesToDays = (startDate = null, endDate = null) => {
    const start = moment.utc(startDate).startOf('day');
    const end = moment.utc(endDate).startOf('day');
    const days = end.diff(start, 'days');
    return days < 0 ? 0 : days;
}

export const convertDaysToDisplayText = (roiData, isPlacementStartDate, isPlacementEndDate) => {
    if (isPlacementStartDate || isPlacementEndDate) {
        const totalDelayDays = roiData?.delayDays;
        var delayText = getWeeksAndDaysFromDelayForm(roiData);
        const dateFormat = isPlacementStartDate ? "Start Date" : "End Date";
        if (totalDelayDays < 0)
            return `${delayText} ${Math.abs(totalDelayDays)} day(s) before the ${dateFormat}`
        return totalDelayDays > 0 ? `${delayText} ${totalDelayDays} day(s) after the ${dateFormat}` : `${delayText} On the ${dateFormat}`
    } else {
        // A wait with no length yet reads as not set.
        const wait = getWeeksDaysHoursAndMinutesFromDelayForm(roiData);
        return wait.trim() ? wait : "not set";
    }
}

const getWeeksAndDaysFromDelayForm = (roiData) => {
    let delayText = '';
    if (roiData?.weeks > 0) delayText = delayText + `${roiData.weeks} week(s) `;
    if (roiData?.days > 0) delayText = delayText + `${roiData.days} day(s) `;
    delayText = delayText + ' delay ---'
    return delayText;
}

const getWeeksDaysHoursAndMinutesFromDelayForm = (roiData) => {
    let delayText = '';
    if (roiData?.weeks > 0) delayText = delayText + `${roiData.weeks} week(s) `;
    if (roiData?.days > 0) delayText = delayText + `${roiData.days} day(s) `;
    if (roiData?.hours > 0) delayText = delayText + `${roiData.hours} hour(s) `;
    if (roiData?.minutes > 0) delayText = delayText + `${roiData.minutes} minute(s) `;

    return delayText;
}

export const useInterval = (callback, delay) => {
    const savedCallback = useRef();

    // Remember the latest function.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
};

//TODO : modify me to work on array structure...
export const checkFirstDelayStep = (diagramStructure) => {
    let data = null;

    const changeDelayStep = diagramStructure.cells.filter(item => item.roiNodeType === RappidNodeType.Delay);

    if (changeDelayStep.length === 0) return null;

    changeDelayStep.forEach(item => {
        if (item.roiNodeType === RappidNodeType.Delay && !data) {
            data = {};
            data = { ...item };
            data.attrs.label.text = RappidText.DelayScheduleStep;
            data.roiData.isFirstDelay = true;
        }
    });
    return data;
};

export const modifyDiagramStructureForDelay = (diagramStructure, delayStepData) => {
    const newDiagramStructure = diagramStructure.cells.map(item => {
        let newItem = { ...item };
        if (item.id === delayStepData.id) {
            newItem = delayStepData;
        }
        return newItem;
    });
    const data = { cells: newDiagramStructure };
    return data;
};

export const checkFirstDelayFrom = (arrayStructure) => {
    const getFirstDelayForm = arrayStructure.filter(item => item.type === RappidNodeType.Delay && item.roiData.isFirstDelay);

    return getFirstDelayForm;
};

export const getScheduleText = (specificDays, hour, timezoneName = null) => {
    var daysString = "";
    for (let i = 0; i < specificDays.length; i++) {
        daysString += dayOfWeekAsString(specificDays[i]);
        if (i === specificDays.length - 1)
            daysString += " "
        else
            daysString += ", "
    }
    daysString += "at " + formatAMPM(hour);
    return "Scheduled: " + daysString;
}

export const getBatchEmailText = (batchSize, delayInHours) => {
    var delayText = "";
    if (batchSize > 0) delayText = delayText + `${batchSize} email(s)/batch `;
    if (delayInHours > 0) delayText = delayText + `${delayInHours} hour(s) `;
    delayText = 'Email Batch: ' + delayText;
    return delayText;
}

const dayOfWeekAsString = (dayIndex) => {
    return ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"][dayIndex] || '';
};

/**
 * Format time as "9 AM" / "2:30 PM". Accepts either a Date or a time string "H:mm" / "H:m"
 * (e.g. from convertedStartHour). Using the time string keeps the node label identical to
 * the form regardless of timezone.
 */
const formatAMPM = (dateOrTimeString) => {
    if (dateOrTimeString == null) return "";

    let hours, minutes = 0;
    if (typeof dateOrTimeString === "string") {
        const parts = dateOrTimeString.trim().split(":");
        hours = parseInt(parts[0], 10);
        minutes = parts[1] ? parseInt(parts[1], 10) : 0;
        if (isNaN(hours)) return "";
    } else {
        const d = new Date(dateOrTimeString);
        if (isNaN(d.getTime())) return "";
        hours = d.getHours();
        minutes = d.getMinutes();
    }

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours || 12;
    return minutes ? `${hours}:${String(minutes).padStart(2, "0")} ${ampm}` : `${hours} ${ampm}`;
};

export const getRecordUpdate = (updateRecordType, automationType) => {
    var recordUpdate = "Record update: by ";
    switch (updateRecordType) {
        case 1:
            if (automationType === Enum.AutomationType.Candidate) {
                return recordUpdate + "Recruiter";
            } else {
                return recordUpdate + "Owner";
            }
        case 2:
            return recordUpdate + "Job owner";
        case 3:
            return recordUpdate + "Admin user";
        case 4:
            return recordUpdate + "No user";
        case 5:
            return recordUpdate + "Placement recruiter";
        case 6:
            return recordUpdate + "Database user";
        case 7:
            return recordUpdate + "Most recent note owner";
        default:
            return "";
    }
};